<template>
  <div class="flex flex-col h-full bg-white dark:bg-slate-900">
    <div class="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
      <div>
        <h1 class="text-2xl font-bold text-slate-900 dark:text-white">Kampanya İstatistikleri</h1>
        <p class="text-sm text-slate-500 dark:text-slate-400">Kampanya performansının detaylı analizi</p>
      </div>
      <woot-button
        variant="outline"
        icon="arrow-download"
        @click="downloadReport"
      >
        Rapor İndir (CSV)
      </woot-button>
      <a
        :href="`/report/${campaignId}`"
        target="_blank"
        class="detailed-report-btn"
      >
        📊 Detaylı Rapor
      </a>
    </div>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <spinner />
    </div>

    <div v-else class="flex-1 overflow-auto p-6">

      <!-- Özet Kartları -->
      <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <div class="stats-card">
          <div class="label">Toplam Hedef</div>
          <div class="value">{{ analytics?.overview?.total_contacts || 0 }}</div>
        </div>
        <div class="stats-card">
          <div class="label">Gönderildi</div>
          <div class="value text-blue-600">{{ analytics?.overview?.sent || 0 }}</div>
        </div>
        <div class="stats-card">
          <div class="label">İletildi</div>
          <div class="value text-green-600">{{ analytics?.overview?.delivered || 0 }}</div>
          <div class="sub-value">%{{ analytics?.delivery?.delivery_rate || 0 }}</div>
        </div>
        <div class="stats-card">
          <div class="label">Okundu</div>
          <div class="value text-yellow-600">{{ analytics?.overview?.read || 0 }}</div>
          <div class="sub-value">%{{ analytics?.delivery?.read_rate || 0 }}</div>
        </div>
        <div class="stats-card">
          <div class="label">Hata</div>
          <div class="value text-red-600">{{ analytics?.overview?.failed || 0 }}</div>
          <div class="sub-value">%{{ analytics?.delivery?.failure_rate || 0 }}</div>
        </div>
      </div>

      <!-- Etiket Filtreleri -->
      <div class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 mb-4">
        <div class="flex flex-wrap items-start gap-4">
          <div class="font-semibold text-sm text-slate-700 dark:text-slate-300 pt-1">🏷️ Filtre:</div>

          <!-- Etiket Checkbox Listesi -->
          <div class="flex flex-wrap gap-2 flex-1">
            <label
              v-for="label in analytics.available_labels"
              :key="label.id"
              class="filter-chip"
              :class="{
                'filter-chip-include': selectedIncludeLabels.includes(label.title),
                'filter-chip-exclude': selectedExcludeLabels.includes(label.title)
              }"
            >
              <input
                type="checkbox"
                :value="label.title"
                v-model="selectedIncludeLabels"
                class="hidden"
                @click.prevent="toggleLabel(label.title)"
              />
              <span class="chip-icon">
                {{ getLabelIcon(label.title) }}
              </span>
              {{ label.title }}
              <span
                class="view-label-link"
                @click.stop="goToLabel(label.title)"
                title="Etiketi görüntüle"
              >↗</span>
            </label>
          </div>

          <!-- Durum Filtresi -->
          <div class="flex items-center gap-2">
            <span class="text-xs text-blue-600">Durum:</span>
            <select v-model="selectedStatus" class="filter-select text-sm">
              <option value="">Tümü</option>
              <option value="pending">Bekliyor</option>
              <option value="sent">Gönderildi</option>
              <option value="delivered">İletildi</option>
              <option value="read">Okundu</option>
              <option value="failed">Başarısız</option>
            </select>
          </div>

          <!-- Filtre Temizle -->
          <button
            v-if="selectedIncludeLabels.length || selectedExcludeLabels.length || selectedStatus"
            @click="clearFilters"
            class="text-xs text-slate-500 hover:text-slate-700 underline"
          >
            Temizle
          </button>
        </div>

        <!-- Filtre açıklama -->
        <div class="text-xs text-slate-500 mt-2 flex items-center gap-2">
          <span>💡 Tıkla: <span class="text-green-600">✓ Var</span> | İkinci tıkla: <span class="text-red-600">✗ Yok</span> | Üçüncü: Temizle</span>
          <span class="ml-auto">{{ filteredContacts.length }} / {{ analytics.contacts?.length || 0 }} kişi</span>
        </div>
      </div>

      <!-- Detaylı Liste -->
      <div class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div class="p-4 border-b border-slate-200 dark:border-slate-700 font-semibold flex items-center justify-between">
          <span>Son İletişim Aktiviteleri ({{ filteredContacts.length }} kişi)</span>
          <span class="live-indicator" :class="{ 'refreshing': isRefreshing }">
            <span class="live-dot"></span>
            <span class="live-text">Canlı</span>
          </span>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse">
            <thead>
              <tr>
                <th>Kişi</th>
                <th>Telefon</th>
                <th>Durum</th>
                <th>Link</th>
                <th>Gönderildi</th>
                <th>İletildi</th>
                <th>Okundu</th>
                <th width="100">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contact in filteredContacts"
                :key="contact.id"
                :class="{
                  'row-locked': contact.is_locked,
                  'row-active-conversation': contact.has_active_conversation && !contact.is_locked
                }"
              >
                <td class="font-medium">
                  {{ contact.contact_name }}
                  <span v-if="contact.is_locked" class="locked-badge" :title="'Düzenleyen: ' + contact.locked_by">
                    🔒
                  </span>
                  <span v-if="contact.has_active_conversation" class="active-badge" title="Aktif sohbet var">
                    💬
                  </span>
                </td>
                <td class="text-sm font-mono">{{ contact.phone }}</td>
                <td>
                  <span :class="['status-badge', contact.status]">
                    {{ normalizeStatus(contact.status) }}
                  </span>
                  <div v-if="contact.error_message" class="text-xs text-red-500 mt-1">
                    {{ contact.error_message }}
                  </div>
                </td>
                <td>
                  <span v-if="contact.link_clicked_at" class="text-green-600 font-medium">✅ Tıklandı</span>
                  <span v-else class="text-slate-400">❌ Tıklanmadı</span>
                </td>
                <td>{{ formatDate(contact.sent_at) }}</td>
                <td>{{ formatDate(contact.delivered_at) }}</td>
                <td>{{ formatDate(contact.read_at) }}</td>
                <td>
                  <div class="row-actions">
                    <!-- Validation error - düzenleme butonu -->
                    <button
                      v-if="contact.status === 'validation_error'"
                      @click="openEditModal(contact)"
                      class="btn-action edit"
                      title="Düzenle ve Gönder"
                    >
                      ✏️
                    </button>

                    <!-- İlk gönderim veya başarısız için -->
                    <button
                      v-if="['pending', 'failed'].includes(contact.status)"
                      @click="retryMessage(contact)"
                      class="btn-action retry"
                      :title="contact.status === 'failed' ? 'Tekrar Dene' : 'Hemen Gönder'"
                    >
                      {{ contact.status === 'failed' ? '🔄' : '🚀' }}
                    </button>

                    <!-- Zaten gönderilmiş için tekrar gönder -->
                    <button
                      v-if="['sent', 'delivered', 'read'].includes(contact.status)"
                      @click="confirmResend(contact)"
                      class="btn-action resend"
                      title="Tekrar Gönder"
                    >
                      🔁
                    </button>

                    <button
                      v-if="contact.conversation_id"
                      @click="goToConversation(contact.conversation_id)"
                      class="btn-action chat"
                      title="Sohbete Git"
                    >
                      💬
                    </button>

                    <!-- wa.me butonu - Kişisel WhatsApp'tan gönder -->
                    <a
                      v-if="contact.phone"
                      :href="`https://wa.me/${contact.phone.replace(/[^0-9]/g, '')}`"
                      target="_blank"
                      class="btn-action whatsapp"
                      title="Kişisel WhatsApp'tan Mesaj Gönder"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#25d366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>

                    <!-- Kişi kartına git -->
                    <button
                      @click="goToContact(contact.contact_id)"
                      class="btn-action contact"
                      title="Kişi Kartını Aç"
                    >
                      👤
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!analytics.contacts || analytics.contacts.length === 0">
                <td colspan="7" class="no-data">Henüz veri yok</td>
              </tr>
            </tbody>
          </table>
        </div>
          <- **Route:** Pagination -->
          <div class="pagination-controls" v-if="analytics.pagination">
            <button @click="prevPage" :disabled="currentPage <= 1" class="pagination-btn">
              ← Önceki
            </button>
            <span class="pagination-info">
              Sayfa {{ currentPage }} / {{ analytics.pagination?.total_pages || 1 }}
              ({{ analytics.pagination?.total_count || 0 }} kişi)
            </span>
            <button @click="nextPage" :disabled="currentPage >= analytics.pagination?.total_pages" class="pagination-btn">
              Sonraki →
            </button>
          </div>
      </div>

    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-content">
        <h3>📝 Eksik Bilgileri Düzenle</h3>
        <p class="modal-subtitle">{{ editingContact?.contact_name }}</p>

        <div v-if="editingContact?.error_message" class="error-info">
          ⚠️ {{ editingContact.error_message }}
        </div>

        <div class="form-fields">
          <div class="form-group">
            <label>Okul Adı</label>
            <input v-model="editForm.okul_adi" placeholder="Okul adını girin" />
          </div>
        </div>

        <div class="modal-actions">
          <button @click="closeEditModal" class="btn-cancel">İptal</button>
          <button @click="saveAndRetry" class="btn-save" :disabled="isSaving">
            {{ isSaving ? 'Gönderiliyor...' : '💾 Kaydet ve Gönder' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStoreGetters } from 'dashboard/composables/store';
import Spinner from 'shared/components/Spinner.vue';
import { useAlert } from 'dashboard/composables';

const route = useRoute();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);
const campaignId = computed(() => route.params.campaignId);

const isLoading = ref(true);
const isRefreshing = ref(false);
const lastUpdated = ref(null);
const analytics = ref({
  overview: {},
  delivery: {},
  contacts: [],
  available_labels: []
});

// Filtre state - çoklu seçim için diziler
const selectedIncludeLabels = ref([]);
const selectedExcludeLabels = ref([]);
const selectedStatus = ref('');

// Etiket toggle mantığı: Tıkla = Var, İkinci tıkla = Yok, Üçüncü = Temizle
const goToLabel = (labelTitle) => {
  // Navigate to the label page with the label title
  const labelSlug = labelTitle.toLowerCase().replace(/ /g, '-');
  window.location.href = `/app/accounts/${accountId.value}/labels/${encodeURIComponent(labelTitle)}`;
};

const toggleLabel = (labelTitle) => {
  const inInclude = selectedIncludeLabels.value.indexOf(labelTitle);
  const inExclude = selectedExcludeLabels.value.indexOf(labelTitle);

  if (inInclude === -1 && inExclude === -1) {
    // Hiçbirinde yok -> Include'a ekle (Var)
    selectedIncludeLabels.value.push(labelTitle);
  } else if (inInclude !== -1) {
    // Include'da var -> Include'dan çıkar, Exclude'a ekle (Yok)
    selectedIncludeLabels.value.splice(inInclude, 1);
    selectedExcludeLabels.value.push(labelTitle);
  } else {
    // Exclude'da var -> Exclude'dan çıkar (Temizle)
    selectedExcludeLabels.value.splice(inExclude, 1);
  }
};

// Etiket ikonu: ✓ (var), ✗ (yok), ○ (seçilmemiş)
const getLabelIcon = (labelTitle) => {
  if (selectedIncludeLabels.value.includes(labelTitle)) return '✓';
  if (selectedExcludeLabels.value.includes(labelTitle)) return '✗';
  return '○';
};

// Filtrelenmiş kişiler
const filteredContacts = computed(() => {
  let contacts = analytics.value.contacts || [];

  // Var etiket filtreleri (AND mantığı - tüm etiketlere sahip olmalı)
  for (const label of selectedIncludeLabels.value) {
    contacts = contacts.filter(c => c.labels?.includes(label));
  }

  // Yok etiket filtreleri (AND mantığı - hiçbir etikete sahip olmamalı)
  for (const label of selectedExcludeLabels.value) {
    contacts = contacts.filter(c => !c.labels?.includes(label));
  }

  // Durum filtresi
  if (selectedStatus.value) {
    contacts = contacts.filter(c => c.status === selectedStatus.value);
  }

  return contacts;
});

const clearFilters = () => {
  selectedIncludeLabels.value = [];
  selectedExcludeLabels.value = [];
  selectedStatus.value = '';
};

// Edit modal
const showEditModal = ref(false);
const editingContact = ref(null);
const editForm = ref({ okul_adi: '' });
const isSaving = ref(false);
const currentPage = ref(1);
const perPage = ref(1000);

const fetchCampaignAnalytics = async () => {
  try {
    const response = await axios.get(`/api/v1/accounts/${accountId.value}/campaigns/${campaignId.value}/analytics?page=${currentPage.value}&per_page=${perPage.value}&label=${selectedIncludeLabels.value[0] || ""}`);
    analytics.value = response.data;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const downloadReport = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/campaigns/${campaignId.value}/export`,
      { responseType: 'blob' }
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `campaign_${campaignId.value}_report.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    useAlert('✅ Rapor indirildi');
  } catch (error) {
    console.error(error);
    useAlert('❌ Rapor indirilemedi');
  }
};


const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchCampaignAnalytics();
  }
};

const nextPage = () => {
  if (currentPage.value < analytics.value.pagination?.total_pages) {
    currentPage.value++;
    fetchCampaignAnalytics();
  }
};
const refreshData = () => {
  fetchCampaignAnalytics();
};

const retryMessage = async (contact) => {
  try {
    await axios.post(`/api/v1/accounts/${accountId.value}/campaigns/${campaignId.value}/retry_contact`, {
      contact_id: contact.contact_id
    });

    useAlert('✅ Gönderim kuyruğa alındı');
    // Refresh shortly after
    setTimeout(refreshData, 1000);
  } catch (error) {
    const errorMessage = error?.response?.data?.error || '❌ Hata oluştu';
    console.error(error);
    useAlert(errorMessage);
  }
};

const confirmResend = (contact) => {
  const sentTime = contact.sent_at ? formatDate(contact.sent_at) : 'daha önce';
  const message = `⚠️ Bu kişiye ${sentTime} tarihinde mesaj gönderildi.\n\nTekrar göndermek istediğinize emin misiniz?`;

  if (window.confirm(message)) {
    retryMessage(contact);
  }
};

const goToConversation = (conversationId) => {
  if (!conversationId) return;
  const url = `/app/accounts/${accountId.value}/conversations/${conversationId}`;
  window.open(url, '_blank');
};

const goToContact = (contactId) => {
  if (!contactId) return;
  const url = `/app/accounts/${accountId.value}/contacts/${contactId}`;
  window.open(url, '_blank');
};

// Label değiştiğinde tekrar fetch et
watch(selectedIncludeLabels, () => {
  currentPage.value = 1;
  fetchCampaignAnalytics();
}, { deep: true });

onMounted(() => {
  fetchCampaignAnalytics();
  // Her 3 saniyede bir otomatik yenile (canlı güncelleme)
  setInterval(() => {
    isRefreshing.value = true;
    fetchCampaignAnalytics().finally(() => {
      isRefreshing.value = false;
      lastUpdated.value = new Date();
    });
  }, 3000);
});

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit'
  }).format(date);
};

const normalizeStatus = (status) => {
  const map = {
    pending: 'Bekliyor',
    sent: 'Gönderildi',
    delivered: 'İletildi',
    read: 'Okundu',
    failed: 'Başarısız',
    validation_error: 'Parametre Hatası',
    replied_positive: 'Olumlu Cevap',
    replied_negative: 'Olumsuz Cevap',
    no_reply: 'Cevap Yok'
  };
  return map[status] || status;
};

// Edit modal functions
const openEditModal = (contact) => {
  editingContact.value = contact;
  editForm.value = { okul_adi: '' };
  showEditModal.value = true;
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingContact.value = null;
};

const saveAndRetry = async () => {
  if (!editingContact.value) return;

  isSaving.value = true;
  try {
    // First update the contact's custom attributes
    await axios.patch(
      `/api/v1/accounts/${accountId.value}/campaign_contacts/${editingContact.value.id}`,
      { custom_attributes: editForm.value }
    );

    // Then retry sending
    await axios.post(
      `/api/v1/accounts/${accountId.value}/campaign_contacts/${editingContact.value.id}/retry`
    );

    useAlert('✅ Bilgiler güncellendi ve mesaj gönderildi');
    closeEditModal();
    setTimeout(refreshData, 1000);
  } catch (error) {
    const errorMessage = error?.response?.data?.error || '❌ Hata oluştu';
    useAlert(errorMessage);
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
.stats-card {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.stats-card .label {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
}

.stats-card .value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.stats-card .sub-value {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

table {
  width: 100%;
}

td, th {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
}

th {
  font-weight: 600;
  color: #6b7280;
  background: #f9fafb;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.sent { background: #dbeafe; color: #1e40af; }
.status-badge.delivered { background: #d1fae5; color: #065f46; }
.status-badge.read { background: #fef3c7; color: #92400e; }
.status-badge.failed { background: #fee2e2; color: #991b1b; }
.status-badge.pending { background: #f3f4f6; color: #4b5563; }
.status-badge.validation_error { background: #fef3c7; color: #92400e; }

.no-data {
  text-align: center;
  color: #9ca3af;
  padding: 40px;
}

.row-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.btn-action {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.btn-action:hover {
  background: #f3f4f6;
}

.btn-action.retry:hover { background: #dbeafe; }
.btn-action.resend:hover { background: #fef3c7; }
.btn-action.chat:hover { background: #d1fae5; }
.btn-action.edit:hover { background: #fef3c7; }
.btn-action.whatsapp { color: #25d366; text-decoration: none; }
.btn-action.whatsapp:hover { background: #dcfce7; }

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 400px;
  max-width: 90%;
}

.modal-content h3 {
  margin: 0 0 4px 0;
  font-size: 18px;
}

.modal-subtitle {
  color: #6b7280;
  margin: 0 0 16px 0;
}

.error-info {
  background: #fef3c7;
  color: #92400e;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
}

.form-fields {
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 12px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #374151;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
}

.btn-save {
  padding: 10px 16px;
  border: none;
  background: #3b82f6;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.btn-save:hover {
  background: #2563eb;
}

.btn-save:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Filter select styles */
.filter-select {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  min-width: 120px;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

/* Filter chip styles */
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.filter-chip:hover {
  background: #f3f4f6;
}

.filter-chip .chip-icon {
  font-size: 11px;
  color: #9ca3af;
}

.filter-chip-include {
  background: #dcfce7;
  border-color: #16a34a;
  color: #166534;
}

.filter-chip-include .chip-icon {
  color: #16a34a;
}

.filter-chip-exclude {
  background: #fee2e2;
  border-color: #dc2626;
  color: #991b1b;
}

.view-label-link {
  margin-left: 6px;
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.view-label-link:hover {
  opacity: 1;
  text-decoration: underline;
}

.filter-chip-exclude .chip-icon {
  color: #dc2626;
}

/* Row status colors */
.row-locked {
  background-color: #fef2f2 !important;
}

.row-locked:hover {
  background-color: #fee2e2 !important;
}

.row-active-conversation {
  background-color: #f0fdf4 !important;
}

.row-active-conversation:hover {
  background-color: #dcfce7 !important;
}

/* Badges */
.locked-badge {
  font-size: 12px;
  margin-left: 4px;
  cursor: help;
}

.active-badge {
  font-size: 12px;
  margin-left: 4px;
  color: #16a34a;
}

/* Contact button */
.btn-action.contact { color: #6366f1; }
.btn-action.contact:hover { background: #eef2ff; }

/* Live indicator */
.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #16a34a;
}

.live-indicator.refreshing {
  background: #fef9c3;
  border-color: #fde047;
  color: #ca8a04;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #22c55e;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

.live-indicator.refreshing .live-dot {
  background: #eab308;
  animation: pulse 0.5s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #334155;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 14px;
  color: #64748b;
}
.detailed-report-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f97316, #ea580c);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  margin-left: 12px;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.3);
}

.detailed-report-btn:hover {
  background: linear-gradient(135deg, #ea580c, #c2410c);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4);
}
</style>
